<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>

    <div class="p-relative header-image pb-5"></div>

    <b-container class="custom-container mt-5">
      <b-row>
        <b-col cols="12" class="px-3">
          <candidates-tabs />

          <h4 class="font-weight-bold pt-4 pb-1">
            {{ isEdit ? 'Edit job' : 'Post a job' }}
          </h4>
          <p class="text-muted mb-4">
            {{
              isEdit
                ? 'Update the details of this job.'
                : template
                ? 'Copied from a closed job. Review the details, then save or publish it as a new job.'
                : 'Tell tradies about the work. Saved as a draft, or published straight away.'
            }}
          </p>

          <b-alert :show="isRemoved" variant="danger">
            This job was removed and can no longer be edited.
          </b-alert>

          <div v-if="!isRemoved && ready" class="job-form-card">
            <job-posting-form
              :job="job"
              :template="template"
              @saved="onSaved"
              @cancel="onCancel"
            />
          </div>
        </b-col>
      </b-row>
    </b-container>
  </b-overlay>
</template>

<script>
import { mapActions } from 'vuex'
import CandidatesTabs from '@/components/candidates/CandidatesTabs.vue'
import JobPostingForm from '@/components/jobPostings/JobPostingForm.vue'
import apiToasts from '@/mixins/apiToasts'

// Create (jobs.new-page) and edit (jobs.edit-page) share this page.
export default {
  name: 'JobsFormPage',

  components: { CandidatesTabs, JobPostingForm },

  mixins: [apiToasts],

  data() {
    return {
      isLoading: false,
      job: null,
      template: null,
    }
  },

  computed: {
    isEdit() {
      return !!this.$route.params.jobId
    },
    ready() {
      if (this.isEdit) return !!this.job
      return !this.$route.query.copy_from || !this.isLoading
    },
    isRemoved() {
      return !!(this.job && this.job.status === 'removed')
    },
  },

  async created() {
    const copyFrom = this.$route.query.copy_from
    if (!this.isEdit && !copyFrom) return
    this.isLoading = true
    try {
      const loaded = await this.fetchJob(
        this.isEdit ? this.$route.params.jobId : copyFrom,
      )
      if (!this.isEdit) {
        this.template = loaded
      } else if (this.isClosed(loaded)) {
        this.showToast(
          'Error',
          'A closed job cannot be edited. Copy it instead.',
          'danger',
        )
        this.$router.replace({
          name: 'jobs.view-job',
          params: { jobId: loaded.id },
        })
        return
      } else {
        this.job = loaded
      }
    } catch (err) {
      this.onError(err, 'Could not load this job.')
      this.$router.replace({ name: 'jobs.list-page' })
    } finally {
      this.isLoading = false
    }
  },

  methods: {
    ...mapActions('jobPosting', ['fetchJob']),

    isClosed(job) {
      return (
        job.status === 'closed' ||
        (job.status === 'published' && !!job.is_expired)
      )
    },

    onSaved(job) {
      this.showToast('Success', this.isEdit ? 'Job updated.' : 'Job saved.')
      this.$router.push({ name: 'jobs.view-job', params: { jobId: job.id } })
    },

    onCancel() {
      if (this.isEdit) {
        this.$router.push({
          name: 'jobs.view-job',
          params: { jobId: this.$route.params.jobId },
        })
      } else {
        this.$router.push({ name: 'jobs.list-page' })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// The form's own section cards carry the surface; this just spaces the page.
.job-form-card {
  margin-bottom: 3rem;
}
</style>
